import React, {
  FC,
  useCallback,
  useEffect,
  useState,
} from 'react';
import cx from 'classnames';
import {
  ExternalLink,
  LangSwitcher,
  Link,
  NavLink,
} from 'components';
import { routes } from 'appConstants';
import { _t, APPLICATION_FORM_EVENT, DOCUMENTS_MODAL_EVENT } from 'utils';
import type { ApplicationFormSource, DocumentsModalItem } from 'utils';

import dippMark from 'assets/img/header-v4/dipp-mark.svg';
import dippText from 'assets/img/header-v4/dipp-text.svg';
import mospromLogo from 'assets/img/header-v4/mosprom.svg';
import iconProjects from 'assets/img/header-v4/icon-projects.svg';
import iconSearch from 'assets/img/header-v4/icon-search.svg';
import iconCtaPlus from 'assets/img/header-v4/icon-cta-plus.svg';
import iconCtaPlusDark from 'assets/img/header-v4/icon-cta-plus-dark.svg';
import iconBurger from 'assets/img/header-v4/icon-burger.svg';

import DocumentsModal from 'containers/Documents/DocumentsModal';
import ProjectsMenu from './ProjectsMenu';
import MainMenu from './MainMenu';
import ApplicationForm from './ApplicationForm';
import styles from './styles.module.scss';

type Props = {
  theme?: 'light' | 'dark';
};

const NAV_LINKS = [
  { to: routes.about.root, title: 'О нас', font: 'montserrat' as const },
  { to: routes.resolutions.root, title: 'Экспортерам', font: 'onest' as const },
  { to: routes.contacts.root, title: 'Контакты', font: 'montserrat' as const },
];

const Header: FC<Props> = ({ theme = 'light' }) => {
  const [isMainMenuOpen, setMainMenuOpen] = useState(false);
  const [isProjectsOpen, setProjectsOpen] = useState(false);
  const [isApplicationOpen, setApplicationOpen] = useState(false);
  const [applicationSource, setApplicationSource] = useState<ApplicationFormSource | null>(null);
  const [isDocumentsOpen, setDocumentsOpen] = useState(false);
  const [documentsItems, setDocumentsItems] = useState<DocumentsModalItem[] | null>(null);
  const [searchValue, setSearchValue] = useState('');

  const closeProjects = useCallback(() => {
    setProjectsOpen(false);
  }, []);

  const closeMainMenu = useCallback(() => {
    setMainMenuOpen(false);
  }, []);

  const closeApplication = useCallback(() => {
    setApplicationOpen(false);
    setApplicationSource(null);
  }, []);

  const closeDocuments = useCallback(() => {
    setDocumentsOpen(false);
    setDocumentsItems(null);
  }, []);

  const openApplication = useCallback((source?: ApplicationFormSource | null) => {
    setApplicationSource(source || { type: 'header' });
    setMainMenuOpen(false);
    setProjectsOpen(false);
    setDocumentsOpen(false);
    setDocumentsItems(null);
    setApplicationOpen(true);
  }, []);

  const onApplicationEvent = useCallback((event: Event) => {
    const { detail } = event as CustomEvent<ApplicationFormSource | null>;
    openApplication(detail);
  }, [openApplication]);

  const openDocuments = useCallback((event?: Event) => {
    const detail = (event as CustomEvent<DocumentsModalItem[] | null> | undefined)?.detail;
    setDocumentsItems(detail && detail.length ? detail : null);
    setMainMenuOpen(false);
    setProjectsOpen(false);
    setApplicationOpen(false);
    setDocumentsOpen(true);
  }, []);

  const openMainMenu = useCallback(() => {
    setProjectsOpen(false);
    setApplicationOpen(false);
    setDocumentsOpen(false);
    setDocumentsItems(null);
    setMainMenuOpen(true);
  }, []);

  const openProjects = useCallback(() => {
    setMainMenuOpen(false);
    setApplicationOpen(false);
    setDocumentsOpen(false);
    setDocumentsItems(null);
    setProjectsOpen(true);
  }, []);

  const closeAllOverlays = useCallback(() => {
    setMainMenuOpen(false);
    setProjectsOpen(false);
    setApplicationOpen(false);
    setApplicationSource(null);
    setDocumentsOpen(false);
    setDocumentsItems(null);
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeAllOverlays();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [closeAllOverlays]);

  useEffect(() => {
    window.addEventListener(APPLICATION_FORM_EVENT, onApplicationEvent);
    return () => window.removeEventListener(APPLICATION_FORM_EVENT, onApplicationEvent);
  }, [onApplicationEvent]);

  useEffect(() => {
    window.addEventListener(DOCUMENTS_MODAL_EVENT, openDocuments);
    return () => window.removeEventListener(DOCUMENTS_MODAL_EVENT, openDocuments);
  }, [openDocuments]);

  return (
    <header className={cx(styles.header, theme === 'light' && styles.light)}>
      <div className={styles.inner}>
        <div className={styles.left}>
          <div className={styles.logos}>
            <ExternalLink
              href="https://www.mos.ru/dipp/"
              className={styles.dipp}
              newTab
            >
              <img src={dippMark} alt="" className={styles.dippMark} />
              <img src={dippText} alt="ДИПП" className={styles.dippText} />
            </ExternalLink>
            <span className={styles.logoDivider} aria-hidden />
            <Link
              to={routes.main.root}
              className={styles.mospromLink}
              onClick={closeAllOverlays}
            >
              <img src={mospromLogo} alt="МОСПРОМ" className={styles.mosprom} />
            </Link>
          </div>

          <div className={styles.controls}>
            <button
              type="button"
              className={styles.projectsBtn}
              aria-expanded={isProjectsOpen}
              aria-haspopup="dialog"
              onClick={openProjects}
            >
              <img src={iconProjects} alt="" className={styles.projectsIcon} />
              <span>{_t('Проекты')}</span>
            </button>

            <div className={styles.search}>
              <label className={styles.srOnly} htmlFor="header-search-v4">
                {_t('Поиск')}
              </label>
              <input
                id="header-search-v4"
                type="search"
                placeholder={_t('Поиск...')}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
              />
              <img src={iconSearch} alt="" className={styles.searchIcon} />
            </div>

            <nav className={styles.nav}>
              {NAV_LINKS.map(({ to, title, font }) => (
                <NavLink
                  key={title}
                  to={to}
                  className={cx(
                    styles.navLink,
                    font === 'montserrat' && styles.navMontserrat,
                  )}
                  activeClassName={styles.navLinkActive}
                >
                  {_t(title)}
                </NavLink>
              ))}
            </nav>
          </div>
        </div>

        <div className={styles.right}>
          <button
            type="button"
            className={styles.cta}
            aria-expanded={isApplicationOpen}
            aria-haspopup="dialog"
            onClick={() => openApplication({ type: 'header' })}
          >
            <span className={styles.ctaLabel}>{_t('Подать заявку')}</span>
            <span className={styles.ctaPlus} aria-hidden>
              <img
                src={theme === 'light' ? iconCtaPlusDark : iconCtaPlus}
                alt=""
                width={12}
                height={12}
              />
            </span>
          </button>

          <LangSwitcher
            theme={theme}
            className={styles.langSwitcher}
          />

          <button
            type="button"
            className={styles.searchIconBtn}
            aria-label={_t('Поиск')}
            onClick={openMainMenu}
          >
            <img src={iconSearch} alt="" />
          </button>

          <button
            type="button"
            className={styles.burger}
            aria-label={_t('Меню')}
            aria-expanded={isMainMenuOpen}
            aria-haspopup="dialog"
            onClick={openMainMenu}
          >
            <img src={iconBurger} alt="" />
          </button>
        </div>
      </div>

      {isProjectsOpen && <ProjectsMenu onClose={closeProjects} />}
      {isMainMenuOpen && (
        <MainMenu
          onClose={closeMainMenu}
          onOpenProjects={openProjects}
          onOpenApplication={() => openApplication({ type: 'header' })}
        />
      )}
      {isApplicationOpen && (
        <ApplicationForm onClose={closeApplication} source={applicationSource} />
      )}
      {isDocumentsOpen && (
        <DocumentsModal onClose={closeDocuments} items={documentsItems || undefined} />
      )}
    </header>
  );
};

export default Header;
